import { adminPost } from "@/lib/kbAdminClient";
import type { KbLane } from "@/lib/kb/types";

type AskMode = "kb" | "contacts" | "location";

export async function persistTrainingThreadTurn(args: {
  organizationId?: string | null;
  threadId: string;
  query: string;
  answer: string;
  answerMode: AskMode;
  queryKind: "new" | "followup";
  needsClarification: boolean;
  sources: Array<{ id: string; title: string; score?: number; url?: string }>;
  contacts: Array<{ source?: string | null; sourceId?: string | null; name?: string | null; contactPath?: string | null }>;
  guidancePurpose?: "contacts" | "policy" | "advice" | "pricing";
  guidanceProduct?: "all" | "sheds" | "steel";
  guidanceSource?: "user_selected" | "auto_rules" | "default";
  confidenceBand?: "high" | "medium" | "low";
  lane?: KbLane | null;
}): Promise<void> {
  const resp = await adminPost<{ ok: true; threadId: string; turnIndex: number }>(
    "/api/kb/admin/action?action=threading",
    {
      action: "appendTurn",
      organizationId: args.organizationId ?? null,
      threadId: args.threadId,
      query: args.query,
      answer: args.answer,
      answerMode: args.answerMode,
      queryKind: args.queryKind,
      needsClarification: args.needsClarification,
      sources: args.sources,
      contacts: args.contacts,
      guidancePurpose: args.guidancePurpose ?? null,
      guidanceProduct: args.guidanceProduct ?? null,
      guidanceSource: args.guidanceSource ?? null,
      confidenceBand: args.confidenceBand ?? null,
      lane: args.lane ?? null,
    }
  );
  if (!resp.ok) throw new Error(resp.error || "Failed to persist thread turn");
}

export async function addTrainingThreadFeedback(args: {
  organizationId?: string | null;
  threadId: string;
  feedbackText: string;
  feedbackKind?: "trainer_note" | "thumbs_down" | "coverage_gap";
  guidancePurpose?: "contacts" | "policy" | "advice" | "pricing";
  guidanceProduct?: "all" | "sheds" | "steel";
  confidenceBand?: "high" | "medium" | "low";
  lane?: KbLane | null;
}): Promise<void> {
  const resp = await adminPost<{ ok: true }>("/api/kb/admin/action?action=threading", {
    action: "addFeedback",
    organizationId: args.organizationId ?? null,
    threadId: args.threadId,
    feedbackText: args.feedbackText,
    feedbackKind: args.feedbackKind ?? "trainer_note",
    guidancePurpose: args.guidancePurpose ?? null,
    guidanceProduct: args.guidanceProduct ?? null,
    confidenceBand: args.confidenceBand ?? null,
    lane: args.lane ?? null,
  });
  if (!resp.ok) throw new Error(resp.error || "Failed to store trainer feedback");
}

export async function escalateTrainingThreadToDev(args: {
  organizationId?: string | null;
  threadId: string;
  trainerFeedback: string;
}): Promise<{ bugId: string }> {
  const resp = await adminPost<{ ok: true; bugId: string }>(
    "/api/kb/admin/action?action=threading",
    {
      action: "escalateToDev",
      organizationId: args.organizationId ?? null,
      threadId: args.threadId,
      trainerFeedback: args.trainerFeedback,
    }
  );
  if (!resp.ok) throw new Error(resp.error || "Failed to escalate thread to dev");
  return { bugId: resp.data.bugId };
}
